import React, { useEffect } from "react";
import { cryptiqueAnalyticsTheme as theme } from "../theme";

/**
 * In-app confirmation dialog. Use instead of window.confirm.
 * @param {boolean} open - Whether the modal is visible
 * @param {string} title - Modal title
 * @param {string} message - Body text
 * @param {string} confirmLabel - Confirm button label (e.g. "Delete")
 * @param {string} cancelLabel - Cancel button label
 * @param {() => void} onConfirm - Called when user confirms
 * @param {() => void} onCancel - Called when user cancels or clicks backdrop
 * @param {boolean} danger - If true, confirm button uses danger (red) style
 */
export default function ConfirmModal({
  open,
  title = "Confirm",
  message = "",
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  onConfirm,
  onCancel,
  danger = false,
}) {
  useEffect(() => {
    if (!open) return;
    const handleEscape = (e) => {
      if (e.key === "Escape") onCancel?.();
    };
    document.addEventListener("keydown", handleEscape);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "";
    };
  }, [open, onCancel]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      style={{ backgroundColor: "rgba(0,0,0,0.4)" }}
      onClick={(e) => e.target === e.currentTarget && onCancel?.()}
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirm-modal-title"
    >
      <div
        className="rounded-xl shadow-xl border max-w-md w-full p-6"
        style={{
          backgroundColor: theme.bgRaised,
          borderColor: theme.border,
          fontFamily: "Raleway, sans-serif",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <h2
          id="confirm-modal-title"
          className="text-lg font-semibold mb-2"
          style={{ color: theme.textPrimary }}
        >
          {title}
        </h2>
        <p className="text-sm mb-6" style={{ color: theme.textSecondary }}>
          {message}
        </p>
        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 rounded-lg text-sm font-medium transition-colors"
            style={{
              color: theme.textSecondary,
              backgroundColor: theme.bgOverlay,
              border: `1px solid ${theme.border}`,
            }}
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            onClick={() => onConfirm?.()}
            className="px-4 py-2 rounded-lg text-sm font-medium transition-colors"
            style={{
              color: "#fff",
              backgroundColor: danger ? "#dc2626" : theme.primaryLight,
              border: "none",
            }}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
